import { Dictionary } from 'vue-router/types/router';
import { CustomerKoCard, SalesDetailCard, QuotationDetailCard, PurchaseDetailCard, ProjectCashFlow } from '@/store/models.def';

/**
 * JavaScript functions for computing derived properties on cards
 */
export const propertyFunctions: Dictionary<(card: any) => string | number> = {};

propertyFunctions.dueLen = (card: CustomerKoCard) => card.koDate - card.dueDate;
propertyFunctions.amountGainLoss = (card: CustomerKoCard) => card.amount + card.gainLoss;
propertyFunctions.profit = (card: SalesDetailCard) => card.amount - card.cost;
propertyFunctions.profitIncludeTax = (card: SalesDetailCard) => card.amount + card.tax - card.cost;
propertyFunctions.amountIncludeTax = (card: any) => card.amount + (card.tax || card.taxAmount || 0);
propertyFunctions.convertedQuotation = (card: QuotationDetailCard) => card.amount - card.outstandingAmount;
propertyFunctions.projectCost = (card: ProjectCashFlow) => card.stockIssue + card.stockReturn
    + card.otherStock + card.expenses;
propertyFunctions.projectCollection = (card: ProjectCashFlow) => card.customerInvoice + card.customerDebit
    + card.customerCredit;
propertyFunctions.projectBilling = (card: ProjectCashFlow) => card.customerPayment + card.customerContra
    + card.customerRefund;

// AR profit calculation: AR uses SL cost since AR module doesn't track cost
// Formula: arAmount - cost (when toggle is off for tax)
// Formula: arAmountWithTax - cost (when toggle is on for tax)
propertyFunctions.arProfit = (card: any) =>
    (card.arAmount || 0) - (card.cost || 0);

propertyFunctions.arProfitIncludeTax = (card: any) =>
    (card.arAmountWithTax || 0) - (card.cost || 0);

/**
 * SQL expressions for computed properties
 * Used in WHERE clauses to filter on computed fields directly in SQL
 * Keys match propertyFunctions keys, values are SQL expressions using UPPERCASE column names
 */
export const propertySqlExpressions: Dictionary<string> = {
	// CustomerKoCard: koDate (FROMDOCDATE) - dueDate (TODOCDUEDATE)
	dueLen: '(TRY_CAST("FROMDOCDATE" AS BIGINT) - TRY_CAST("TODOCDUEDATE" AS BIGINT))',

	// CustomerKoCard: amount (KOLOCALAMT) + gainLoss (KOGAINLOSS)
	amountGainLoss: '(TRY_CAST("KOLOCALAMT" AS DOUBLE) + TRY_CAST("KOGAINLOSS" AS DOUBLE))',

	// SalesDetailCard: amount (AMOUNT) - cost (COST)
	profit: '(TRY_CAST("AMOUNT" AS DOUBLE) - TRY_CAST("COST" AS DOUBLE))',

	// SalesDetailCard: amount (AMOUNT) + tax (TAX) - cost (COST)
	profitIncludeTax: '(TRY_CAST("AMOUNT" AS DOUBLE) + TRY_CAST("TAX" AS DOUBLE) - TRY_CAST("COST" AS DOUBLE))',

	// Generic: amount + tax
	amountIncludeTax: '(TRY_CAST("AMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("TAX" AS DOUBLE), 0))',

	// QuotationDetailCard: amount (AMOUNT) - outstandingAmount (OUTSTANDINGAMOUNT)
	convertedQuotation: '(TRY_CAST("AMOUNT" AS DOUBLE) - TRY_CAST("OUTSTANDINGAMOUNT" AS DOUBLE))',

	// ProjectCashFlow: stockIssue + stockReturn + otherStock + expenses
	projectCost: '(TRY_CAST("STOCKISSUE" AS DOUBLE) + TRY_CAST("STOCKRETURN" AS DOUBLE) + TRY_CAST("OTHERSTOCK" AS DOUBLE) + TRY_CAST("EXPENSES" AS DOUBLE))',

	// ProjectCashFlow: customerInvoice + customerDebit + customerCredit
	projectCollection: '(TRY_CAST("CUSTOMERINVOICE" AS DOUBLE) + TRY_CAST("CUSTOMERDEBIT" AS DOUBLE) + TRY_CAST("CUSTOMERCREDIT" AS DOUBLE))',

	// ProjectCashFlow: customerPayment + customerContra + customerRefund
	projectBilling: '(TRY_CAST("CUSTOMERPAYMENT" AS DOUBLE) + TRY_CAST("CUSTOMERCONTRA" AS DOUBLE) + TRY_CAST("CUSTOMERREFUND" AS DOUBLE))',

	// AR profit: arAmount - cost
	arProfit: '(COALESCE(TRY_CAST("ARAMOUNT" AS DOUBLE), 0) - COALESCE(TRY_CAST("COST" AS DOUBLE), 0))',

	// AR profit with tax: arAmountWithTax - cost
	arProfitIncludeTax: '(COALESCE(TRY_CAST("ARAMOUNTWITHTAX" AS DOUBLE), 0) - COALESCE(TRY_CAST("COST" AS DOUBLE), 0))',
};
